import { AttioCompanyService } from './AttioCompanyService';
import { AttioConsultantService } from './AttioConsultantService';
import { AttioDealService } from './AttioDealService';
import { AttioEngagementService } from './AttioEngagementService';
import { AttioNotesService } from './AttioNotesService';
import { AttioPeopleService } from './AttioPeopleService';
import { AttioStatusService } from './AttioStatusService';
import { AttioTeamService } from './AttioTeamService';
import { AttioUserService } from './AttioUserService';
import { AttioValuationDetailService } from './AttioValuationDetailService';
import { AttioValuationService } from './AttioValuationService';

/**
 * Main Attio service that combines all individual services
 * Provides a single entry point for all Attio-related operations
 */
export class AttioService {
  // Service instances
  public readonly valuations: AttioValuationService;
  public readonly engagement: AttioEngagementService;
  public readonly consultants: AttioConsultantService;
  public readonly notes: AttioNotesService;
  public readonly status: AttioStatusService;
  public readonly team: AttioTeamService;
  public readonly users: AttioUserService;
  public readonly companies: AttioCompanyService;
  public readonly people: AttioPeopleService;
  public readonly deals: AttioDealService;
  public readonly valuationDetails: AttioValuationDetailService;

  constructor() {
    // Initialize all services
    this.valuations = new AttioValuationService();
    this.engagement = new AttioEngagementService();
    this.consultants = new AttioConsultantService();
    this.notes = new AttioNotesService();
    this.status = new AttioStatusService();
    this.team = new AttioTeamService();
    this.users = new AttioUserService();
    this.companies = new AttioCompanyService();
    this.people = new AttioPeopleService();
    this.deals = new AttioDealService();
    this.valuationDetails = new AttioValuationDetailService();
  }

  /**
   * Get service health status
   */
  async getHealthStatus(): Promise<{ service: string; status: 'healthy' | 'unhealthy' }[]> {
    const services = [
      { name: 'valuations', service: this.valuations },
      { name: 'engagement', service: this.engagement },
      { name: 'consultants', service: this.consultants },
      { name: 'notes', service: this.notes },
      { name: 'status', service: this.status },
      { name: 'team', service: this.team },
      { name: 'users', service: this.users },
      { name: 'companies', service: this.companies },
      { name: 'people', service: this.people },
      { name: 'deals', service: this.deals },
      { name: 'valuationDetails', service: this.valuationDetails },
    ];

    const healthChecks = await Promise.allSettled(
      services.map(async ({ name, service }) => {
        try {
          // Simple health check - try to access a basic property
          if (service && typeof service === 'object') {
            return { service: name, status: 'healthy' as const };
          }
          return { service: name, status: 'unhealthy' as const };
        } catch {
          return { service: name, status: 'unhealthy' as const };
        }
      })
    );

    return healthChecks.map((result, index) => {
      if (result.status === 'fulfilled') {
        return result.value;
      }
      return { service: services[index].name, status: 'unhealthy' as const };
    });
  }
}

// Export singleton instance
export const attioService = new AttioService();
